/**
 * Context-aware service wrapper for RLS-enabled operations
 * 
 * This module provides utilities to ensure that database operations
 * are performed with the proper context set for RLS policies.
 */

import { contextManager } from '@/lib/contextManager';
import { withRLSErrorHandling } from '@/lib/rlsErrorHandler';

/**
 * Execute a database operation with referent context
 */
export async function withReferentContext<T>(
  referentId: string,
  operation: () => Promise<T>
): Promise<T> {
  // Set the referent context
  await contextManager.setReferentContext(referentId);
  
  try {
    // Execute the operation with RLS error handling
    return await withRLSErrorHandling(operation, 'referent');
  } finally {
    // Clean up context (optional, as context is session-scoped)
    // await contextManager.clearContext();
  }
}

/**
 * Execute a database operation with campaign context
 */
export async function withCampaignContext<T>(
  campaignLink: string,
  operation: () => Promise<T>
): Promise<T> {
  // Set the campaign context
  await contextManager.setCampaignContext(campaignLink);
  
  try {
    // Execute the operation with RLS error handling
    return await withRLSErrorHandling(operation, 'campaign');
  } finally {
    // Clean up context (optional, as context is session-scoped)
    // await contextManager.clearContext();
  }
}

/**
 * Execute a database operation with existing context (no context setting)
 * This is useful when context is already set by hooks
 */
export async function withExistingContext<T>(
  operation: () => Promise<T>,
  contextType: 'referent' | 'campaign' = 'referent'
): Promise<T> {
  return await withRLSErrorHandling(operation, contextType);
}

/**
 * Validate that the required context is set before executing an operation
 * PHASE 2: Système de contexte obsolète - on exécute directement l'opération
 * La sécurité est maintenant assurée par les RLS policies au niveau base de données
 */
export async function validateContextAndExecute<T>(
  operation: () => Promise<T>,
  contextType: 'referent' | 'campaign' = 'referent'
): Promise<T> {
  // PHASE 2: Plus de validation de contexte - les méthodes getCurrentReferentId et 
  // getCurrentCampaignLink sont dépréciées et retournent toujours null.
  // La sécurité est maintenant assurée par les RLS policies et les tokens dans les RPC.
  
  // Exécuter directement l'opération sans validation de contexte
  return await withExistingContext(operation, contextType);
}